import React, { useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from 'react-router-dom';
import { allUserListApi, blockVenderApi, deleteVenderApi, resumeVenderApi } from '../../api';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import { AdminTable, ActionCell } from '../../common/adminTable';
import Paginations from '../../common/pagination/Paginations';
import configuration from '../../config/Config';
import UserImg from '../../common/assests/reyleafLogo.svg';
import './Vender.css';

const getStatusLabel = (status) => {
  switch (status) {
    case 1: return 'Unverified';
    case 2: return 'Active';
    case 3: return 'Blocked';
    case 4: return 'Deleted';
    case 5: return 'Rejected';
    default: return '-';
  }
};

const AllVendor = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [venderData, setVenderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [change, setChange] = useState(false);

  const handleClickOpen = (row) => {
    navigate(`/vender-management/vender-details/${row?._id}`, { state: { selectedRow: row } });
  };

  const handleAllVenderList = async () => {
    try {
      setLoading(true);
      const response = await allUserListApi({ token, type: 2, currentPage });
      setVenderData(response?.users || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllVenderList();
  }, [currentPage, change]);

  const handleBlockVender = async (id) => {
    const isConfirmed = await confirm({ title: "Block Vendor", description: "Are you sure you want to block this vendor?", confirmText: "Block" });
    if (!isConfirmed) return;
    try {
      const response = await blockVenderApi({ token, id });
      setChange(!change);
      toast.success(response?.message);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Vendor not Blocked");
    }
  };

  const handleResumeVender = async (id) => {
    const isConfirmed = await confirm({ title: "Resume Vendor", description: "Are you sure you want to resume this vendor?", confirmText: "Resume" });
    if (!isConfirmed) return;
    try {
      const response = await resumeVenderApi({ token, id });
      setChange(!change);
      toast.success(response?.message);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Vendor not resumed");
    }
  };

  const handleDeleteVender = async (id) => {
    const isConfirmed = await confirm({ title: "Delete Vendor", description: "Are you sure you want to delete this vendor?", confirmText: "Delete", destructive: true });
    if (!isConfirmed) return;
    try {
      const response = await deleteVenderApi({ token, id });
      setChange(!change);
      toast.success(response?.message);
    } catch (error) {
      toast.error(error?.message || "Vendor not deleted");
    }
  };

  const columns = [
    {
      field: "fullname",
      headerName: "Business Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={row.picture ? `${configuration.cdnFront}/${row.picture}` : UserImg}
            alt={row.fullname || "Vendor"}
            style={{ height: 36, width: 36, borderRadius: "50%", flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", minWidth: 160, renderCell: (r) => r.email || "-" },
    { field: "phone", headerName: "Mobile No", width: 120, renderCell: (r) => r.phone || "-" },
    { field: "bankdetail", headerName: "Bank Details", width: 130, renderCell: (r) => r.bankdetail || "-" },
    { field: "account_status", headerName: "Status", width: 100, renderCell: (r) => getStatusLabel(r.account_status) },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) =>
        r.address ? [r.address.address || "-"].filter(Boolean).join(", ") : "-",
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 120,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US") : "-",
    },
    {
      field: "__actions",
      headerName: "",
      width: 120,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
            row.account_status === 3
              ? {
                  label: "Resume",
                  icon: <LockOpenIcon fontSize="small" />,
                  color: "#065F46",
                  onClick: () => handleResumeVender(row._id),
                }
              : {
                  label: "Block",
                  icon: <LockIcon fontSize="small" />,
                  color: "#362B24",
                  onClick: () => handleBlockVender(row._id),
                },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDeleteVender(row._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
      <AdminTable
        columns={columns}
        rows={venderData}
        loading={loading}
        rowKey="_id"
        emptyText="No vendors found"
        rowSx={(row) => row.account_status === 3 ? { opacity: 0.65 } : {}}
      />
      <Paginations
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
        disabled={loading}
      />
    </Card>
  );
};

export default AllVendor;
